import { apiFetch } from '../utils/apiClient';

const ACCOUNT_API_BASE =
  process.env.REACT_APP_Auth_Url || 'https://momentumampapi-stage.insurancegig.com';


const BASE = `${ACCOUNT_API_BASE}/api/Marketplace`;

export interface MarketplaceModuleResponse {
  moduleId: string;
  name: string;
  moduleType?: string;
  category: string;
  icon: string;
  iconColor: string;
  description: string;
  overview: string;
  documentation: string;
  changelog: string;
  tags: string[];
  pricing: string;
  pricingType: string;
  publisher: string;
  publisherVerified: boolean;
  usageLevel: string;
  reliability: string;
  maturity: string;
  satisfaction: string;
  isPremade: boolean;
  reviewCount: number;
  installed: boolean;
  isEnabled: boolean;
  updatedAt: string;
}

export interface MarketplaceReview {
  reviewerName: string;
  rating: number;
  comment: string;
  createdAt: string;
}

export interface ModuleDetailResponse {
  module: MarketplaceModuleResponse;
  reviews: MarketplaceReview[];
}

export async function getMarketplaceModules(
  category?: string,
  isPremade?: boolean,
  search?: string,
): Promise<MarketplaceModuleResponse[]> {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (isPremade !== undefined) params.set('isPremade', String(isPremade));
  if (search) params.set('search', search);
  const qs = params.toString();
  return apiFetch<MarketplaceModuleResponse[]>(`${BASE}/modules${qs ? `?${qs}` : ''}`);
}

export async function getModuleDetail(moduleId: string): Promise<ModuleDetailResponse> {
  return apiFetch<ModuleDetailResponse>(`${BASE}/modules/${encodeURIComponent(moduleId)}`);
}

export async function installModule(moduleId: string, isEnabled = true): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`${BASE}/install`, {
    method: 'POST',
    body: JSON.stringify({ moduleId, isEnabled }),
  });
}

export async function uninstallModule(moduleId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(
    `${BASE}/uninstall/${encodeURIComponent(moduleId)}`,
    { method: 'DELETE' },
  );
}

export async function toggleModule(
  moduleId: string,
  isEnabled: boolean,
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`${BASE}/toggle`, {
    method: 'PUT',
    body: JSON.stringify({ moduleId, isEnabled }),
  });
}

export async function getModuleReviews(moduleId: string): Promise<MarketplaceReview[]> {
  return apiFetch<MarketplaceReview[]>(
    `${BASE}/reviews/${encodeURIComponent(moduleId)}`,
  );
}

export async function submitReview(
  moduleId: string,
  reviewerName: string,
  rating: number,
  comment: string,
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`${BASE}/reviews`, {
    method: 'POST',
    body: JSON.stringify({ moduleId, reviewerName, rating, comment }),
  });
}

export interface ExternalMcpItem {
  userId: number;
  companyId: number;
  transactionId: string;
  name: string;
  description: string;
  url: string;
  tools: string;
  headers?: string;
  hasHeaders?: boolean;
  firstName?: string;
  lastName?: string;
  created?: string;
  needsUserCredentials?: boolean;
  isSystem?: boolean;
}

export async function listExternalMcp(): Promise<ExternalMcpItem[]> {
  return apiFetch<ExternalMcpItem[]>(`${ACCOUNT_API_BASE}/api/externalmcp/list`);
}

/**
 * Collapses rows that share the same server URL down to the most recently created one.
 * Guards against orphaned ExternalMCP rows (e.g. from a failed uninstall cleanup) showing
 * up as duplicate entries anywhere this list is rendered.
 */
export function dedupeExternalMcps<T extends { url: string; created?: string }>(items: T[]): T[] {
  const byUrl = new Map<string, T>();
  for (const item of items) {
    const key = item.url.trim().toLowerCase();
    const existing = byUrl.get(key);
    if (!existing) {
      byUrl.set(key, item);
      continue;
    }
    const existingCreated = existing.created ? Date.parse(existing.created) : NaN;
    const itemCreated = item.created ? Date.parse(item.created) : NaN;
    if (!Number.isNaN(itemCreated) && (Number.isNaN(existingCreated) || itemCreated > existingCreated)) {
      byUrl.set(key, item);
    }
  }
  return Array.from(byUrl.values());
}

export interface ExternalMcpHeader {
  [key: string]: string;
}

export async function saveExternalMcp(payload: {
  name: string;
  description: string;
  url: string;
  headers: ExternalMcpHeader[] | null;
}): Promise<{ success: boolean; errorMessage?: string }> {
  const transactionId = crypto.randomUUID().replace(/-/g, '');
  return apiFetch<{ success: boolean; errorMessage?: string }>(`${ACCOUNT_API_BASE}/api/externalmcp/save`, {
    method: 'POST',
    body: JSON.stringify({ transactionId, ...payload }),
  });
}

export async function editExternalMcp(payload: {
  transactionId: string;
  name: string;
  description: string;
  url: string;
  headers: ExternalMcpHeader[] | null;
}): Promise<{ success: boolean; errorMessage?: string }> {
  return apiFetch<{ success: boolean; errorMessage?: string }>(`${ACCOUNT_API_BASE}/api/externalmcp/edit`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function deleteExternalMcp(transactionId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`${ACCOUNT_API_BASE}/api/externalmcp/delete/${transactionId}`, {
    method: 'POST',
  });
}

export interface InstalledSkillItem {
  id: string;
  skillId: string;
  name: string;
  description: string;
  icon: string;
  category: string;
}

// Skill = 0 in ToolType (see MyToolsPage.tsx's TOOL_TYPE_TO_INT) — filtered client-side since
// /api/MyTools/installed returns every installed tool type, not skills alone.
export async function listInstalledSkills(): Promise<InstalledSkillItem[]> {
  const raw = await apiFetch<any>(`${ACCOUNT_API_BASE}/api/MyTools/installed`);
  const list: any[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
  return list
    .filter(t => t.type === 0)
    .map(t => ({
      id: String(t.id),
      // The real Anthropic skill_id (MyToolModel.SkillId) — not the my_tools row's
      // own PK above. This is what MyAMP's claude_managed_skills is keyed by.
      skillId: String(t.skillId ?? t.skill_id ?? ''),
      name: t.name ?? '',
      description: t.description ?? '',
      icon: t.icon ?? '',
      category: t.category ?? '',
    }));
}
